import { useState, useEffect } from 'react';
import api from '../utils/api';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/analytics/dashboard');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#2C4C3B] border-r-transparent"></div>
        </div>
      </Layout>
    );
  }

  const pipelineData = [
    { name: 'Trial', value: analytics?.trial_customers || 0, color: '#3B82F6' },
    { name: 'Follow-up', value: analytics?.followup_customers || 0, color: '#EAB308' },
    { name: 'Purchased', value: analytics?.purchased_customers || 0, color: '#22C55E' }
  ];

  const occupancyData = [
    { name: 'Occupied', value: analytics?.beds_occupied || 0 },
    { name: 'Available', value: (analytics?.bed_capacity || 15) - (analytics?.beds_occupied || 0) }
  ];

  return (
    <Layout>
      <div data-testid="analytics-page">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-[#2C4C3B] mb-2">Analytics</h1>
          <p className="text-sm sm:text-base text-[#1C1C1A]/60">Detailed insights and performance metrics</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="p-6 bg-white border border-[#E5E5DF] rounded-lg">
            <h2 className="text-xl font-semibold tracking-tight text-[#2C4C3B] mb-6">Customer Pipeline</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={pipelineData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5DF" />
                <XAxis dataKey="name" stroke="#1C1C1A" />
                <YAxis stroke="#1C1C1A" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E5DF',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                  {pipelineData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card className="p-6 bg-white border border-[#E5E5DF] rounded-lg">
            <h2 className="text-xl font-semibold tracking-tight text-[#2C4C3B] mb-6">Bed Occupancy</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={occupancyData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={100}
                  fill="#2C4C3B"
                  dataKey="value"
                >
                  <Cell fill="#2C4C3B" />
                  <Cell fill="#E2E8E4" />
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 bg-white border border-[#E5E5DF] rounded-lg">
            <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]/60 mb-2">Total Customers</h3>
            <p className="text-4xl font-semibold tracking-tight text-[#2C4C3B]">{analytics?.total_customers || 0}</p>
          </Card>

          <Card className="p-6 bg-white border border-[#E5E5DF] rounded-lg">
            <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]/60 mb-2">Conversion Rate</h3>
            <p className="text-4xl font-semibold tracking-tight text-[#C05C42]">{analytics?.conversion_rate || 0}%</p>
          </Card>

          <Card className="p-6 bg-white border border-[#E5E5DF] rounded-lg">
            <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]/60 mb-2">Today&apos;s Attendance</h3>
            <p className="text-4xl font-semibold tracking-tight text-[#2C4C3B]">{analytics?.today_attendance || 0}</p>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Analytics;